/**
 * Double Elimination Tournament Component
 *
 * 더블 엘리미네이션 토너먼트 브라켓을 렌더링하는 컴포넌트
 */

import React from 'react';
import '@assets/css/comp-form-trmntBracket.css';
import '@assets/css/competition.css';

const CompDoubleElimination = ({
    tournamentData,
    theme = 'light',
    onPlayerClick,
    isFromHome = false,
    competitionInfo = null
}) => {
    /**
     * 시드를 타입별로 그룹화
     */
    const groupBySeedType = () => {
        const grouped = {
            winner: [],
            loser: [],
            loserFinal: [],
            grandFinal: []
        };

        tournamentData.forEach(seed => {
            if (seed.seedType === 'W') {
                grouped.winner.push(seed);
            } else if (seed.seedType === 'L') {
                grouped.loser.push(seed);
            } else if (seed.seedType === 'LF') {
                grouped.loserFinal.push(seed);
            } else if (seed.seedType === 'GF') {
                grouped.grandFinal.push(seed);
            }
        });

        return grouped;
    };

    /**
     * 승자조를 라운드별로 그룹화
     */
    const groupWinnerByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);
        const rounds = [];
        let currentRoundSize = Math.ceil(sortedMatches.length / 2);
        let matchIndex = 0;

        while (matchIndex < sortedMatches.length) {
            const round = [];
            const takeCount = Math.min(currentRoundSize, sortedMatches.length - matchIndex);

            for (let i = 0; i < takeCount; i++) {
                round.push(sortedMatches[matchIndex++]);
            }

            rounds.push(round);
            currentRoundSize = Math.ceil(currentRoundSize / 2);
        }

        return rounds;
    };

    /**
     * 패자조를 라운드별로 그룹화
     */
    const groupLoserByRound = (matches) => {
        if (!matches || matches.length === 0) return [];

        const sortedMatches = [...matches].sort((a, b) => a.seedNo - b.seedNo);
        const firstLoserSeedNo = sortedMatches[0].seedNo;

        const loserRoundRanges = {
            4: [
                { start: 4, end: 4, round: 1 }
            ],
            8: [
                { start: 8, end: 9, round: 1 },
                { start: 10, end: 11, round: 2 },
                { start: 12, end: 12, round: 3 }
            ],
            16: [
                { start: 16, end: 19, round: 1 },
                { start: 20, end: 23, round: 2 },
                { start: 24, end: 25, round: 3 },
                { start: 26, end: 27, round: 4 },
                { start: 28, end: 28, round: 5 }
            ],
            32: [
                { start: 32, end: 39, round: 1 },
                { start: 40, end: 47, round: 2 },
                { start: 48, end: 51, round: 3 },
                { start: 52, end: 55, round: 4 },
                { start: 56, end: 57, round: 5 },
                { start: 58, end: 59, round: 6 },
                { start: 60, end: 60, round: 7 }
            ]
        };

        let tournamentSize = 4;
        if (firstLoserSeedNo >= 32) {
            tournamentSize = 32;
        } else if (firstLoserSeedNo >= 16) {
            tournamentSize = 16;
        } else if (firstLoserSeedNo >= 8) {
            tournamentSize = 8;
        }

        const roundRanges = loserRoundRanges[tournamentSize];

        const loserWithRound = sortedMatches.map(loserMatch => {
            let assignedRound = 0;
            for (const range of roundRanges) {
                if (loserMatch.seedNo >= range.start && loserMatch.seedNo <= range.end) {
                    assignedRound = range.round;
                    break;
                }
            }
            return {
                ...loserMatch,
                round: assignedRound
            };
        });

        const maxRound = Math.max(...loserWithRound.map(m => m.round));
        const loserRounds = [];

        for (let r = 1; r <= maxRound; r++) {
            const roundMatches = loserWithRound
                .filter(m => m.round === r)
                .sort((a, b) => a.seedNo - b.seedNo);
            if (roundMatches.length > 0) {
                loserRounds.push(roundMatches);
            }
        }

        return loserRounds;
    };

    /**
     * 팔 타입을 한글로 변환
     */
    const getArmTypeNm = (armType) => {
        if (armType === 'L') {
            return '왼팔';
        } else if (armType === 'R') {
            return '오른팔';
        }
        return armType;
    };

    /**
     * 상태를 한글로 변환
     */
    const getStatusNm = (statusCd) => {
        if (statusCd === 'C' || statusCd === 'R') {
            return '대기중';
        } else if (statusCd === 'E') {
            return '종료';
        } else {
            return '진행중';
        }
    };

    /**
     * 매치 렌더링
     */
    const renderMatch = (seed, roundIndex) => {
        const isPlayerAWinner = seed.winPlyr === seed.plyrCdA;
        const isPlayerBWinner = seed.winPlyr === seed.plyrCdB;
        const hasWinner = seed.winPlyr !== null && seed.winPlyr !== undefined;
        const canSelect = seed.plyrCdA || seed.plyrCdB;
        const isClickable = canSelect;

        return (
            <div
                key={`${seed.seedType}-${seed.seedNo}`}
                className={`bracket-match ${hasWinner ? 'completed' : ''} ${isClickable ? 'selectable' : ''}`}
                data-seed={seed.seedNo}
                data-round={roundIndex}
            >
                <div className="match-number" style={{color: theme === 'dark' ? '#a0a0a0' : '#6c757d'}}>
                    <span style={{ fontSize: '15px' }}>#{seed.seedNo}</span>
                    {!seed.plyrCdA && !seed.plyrCdB && (
                        <div className="match-waiting">
                            <small style={{ fontSize: '9px' }}>대기 중</small>
                        </div>
                    )}
                </div>

                <div className="match-players-container">
                    <div
                        className={`match-player ${isPlayerAWinner ? 'winner' : ''} ${!seed.plyrCdA || seed.plyrCdA === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && onPlayerClick(seed, 'A')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmA || 'BYE'}
                        </span>
                        {isPlayerAWinner && <span className="winner-icon">👑</span>}
                    </div>

                    <div
                        className={`match-player ${isPlayerBWinner ? 'winner' : ''} ${!seed.plyrCdB || seed.plyrCdB === 'null' ? 'bye' : ''} ${isClickable ? 'clickable' : ''}`}
                        onClick={() => isClickable && onPlayerClick(seed, 'B')}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                    >
                        <span className="player-name">
                            {seed.plyrNmB || 'BYE'}
                        </span>
                        {isPlayerBWinner && <span className="winner-icon">👑</span>}
                    </div>
                </div>
            </div>
        );
    };

    /**
     * 더블 엘리미네이션 렌더링
     */
    const renderDoubleElimination = () => {
        const grouped = groupBySeedType();
        const winnerRounds = groupWinnerByRound(grouped.winner);
        const loserRounds = groupLoserByRound(grouped.loser);
        const loserFinals = grouped.loserFinal;
        const grandFinals = grouped.grandFinal;

        return (
            <div className={`tournament-bracket-wrapper double-elimination ${theme === 'dark' ? 'theme-dark' : 'theme-light'}`}>
                {/* Home에서 온 경우 대회 정보 헤더 */}
                {isFromHome && competitionInfo && (<>
                    <div className="bracket-section d-flex gap-1 flex-wrap mb-2 pb-0">
                        {competitionInfo.dvsnNm && (
                            <span className="badge bg-info" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                                <i className="bi bi-tag-fill me-1"></i>
                                {competitionInfo.dvsnNm}
                            </span>
                        )}
                        {competitionInfo.wetclsNm && (
                            <span className="badge bg-secondary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                                <i className="bi bi-speedometer2 me-1"></i>
                                {competitionInfo.wetclsNm}
                            </span>
                        )}
                        <span className="badge bg-primary" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                            <i className="bi bi-hand-index-thumb me-1"></i>
                            {getArmTypeNm(competitionInfo.armType)}
                        </span>
                        <span className={`badge ${competitionInfo.mtmkgStat === 'E' ? 'bg-secondary' : competitionInfo.mtmkgStat === 'C' || competitionInfo.mtmkgStat === 'R' ? 'bg-warning text-dark' : 'bg-success'}`} style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                            {getStatusNm(competitionInfo.mtmkgStat)}
                        </span>
                    </div>
                </>)}

                {/* 승자조 */}
                <div className="bracket-section mb-4">
                    <div className="section-header bg-success text-white p-2 rounded">
                        <h5 className="mb-0">🏆 승자조 (Winner Bracket)</h5>
                    </div>
                    <div className="bracket-rounds-container mt-2">
                        {winnerRounds.map((round, roundIndex) => {
                            let roundName = '';
                            const totalRounds = winnerRounds.length;

                            if (roundIndex === totalRounds - 1) {
                                roundName = 'W-결승';
                            } else if (roundIndex === totalRounds - 2) {
                                roundName = 'W-준결승';
                            } else if (roundIndex === totalRounds - 3) {
                                roundName = 'W-8강';
                            } else if (roundIndex === totalRounds - 4) {
                                roundName = 'W-16강';
                            } else if (roundIndex === totalRounds - 5) {
                                roundName = 'W-32강';
                            } else {
                                roundName = `W-R${roundIndex + 1}`;
                            }

                            return (
                            <div key={`winner-round-${roundIndex}`} className="bracket-round">
                                <div className="round-title">
                                    <span className="badge bg-success">{roundName}</span>
                                </div>
                                <div className="round-matches">
                                    {round.map(seed => renderMatch(seed, roundIndex))}
                                </div>
                                {roundIndex < winnerRounds.length - 1 && (
                                    <div className="round-connector">
                                        <svg className="connector-lines" width="100%" height="100%">
                                            {round.map((seed, idx) => {
                                                if (idx % 2 === 0 && idx + 1 < round.length) {
                                                    return (
                                                        <g key={`w-connector-${idx}`}>
                                                            <line x1="0" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="0" y1={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="50%" y1={`${(idx * 100 / round.length) + (50 / round.length)}%`}
                                                                  x2="50%" y2={`${((idx + 1) * 100 / round.length) + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                            <line x1="50%"
                                                                  y1={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                                  x2="100%"
                                                                  y2={`${((idx * 100 / round.length) + ((idx + 1) * 100 / round.length)) / 2 + (50 / round.length)}%`}
                                                                  stroke="#198754" strokeWidth="2"/>
                                                        </g>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </svg>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>

                {/* 패자조 */}
                {loserRounds.length > 0 && (
                    <div className="bracket-section mb-4">
                        <div className="section-header bg-warning text-white p-2 rounded">
                            <h5 className="mb-0">⚔️ 패자조 (Loser Bracket)</h5>
                        </div>
                        <div className="bracket-rounds-container mt-2">
                            {loserRounds.map((round, roundIndex) => (
                                <div key={`loser-round-${roundIndex}`} className="bracket-round">
                                    <div className="round-title">
                                        <span className="badge bg-warning text-dark">L-R{roundIndex + 1}</span>
                                    </div>
                                    <div className="round-matches">
                                        {round.map(seed => renderMatch(seed, roundIndex))}
                                    </div>
                                    {roundIndex < loserRounds.length - 1 && (
                                        <div className="round-connector">
                                            <svg className="connector-lines" width="100%" height="100%">
                                                {round.map((currentSeed, currentIdx) => {
                                                    if (!currentSeed.seedWinNo) return null;

                                                    let targetRoundIndex = -1;
                                                    let targetMatchIndex = -1;

                                                    for (let r = roundIndex + 1; r < loserRounds.length; r++) {
                                                        const idx = loserRounds[r].findIndex(s => s.seedNo === currentSeed.seedWinNo);
                                                        if (idx >= 0) {
                                                            targetRoundIndex = r;
                                                            targetMatchIndex = idx;
                                                            break;
                                                        }
                                                    }

                                                    if (targetRoundIndex === roundIndex + 1 && targetMatchIndex >= 0) {
                                                        const nextRound = loserRounds[targetRoundIndex];
                                                        const currentY = (currentIdx / round.length) * 100 + (50 / round.length);
                                                        const nextY = (targetMatchIndex / nextRound.length) * 100 + (50 / nextRound.length);

                                                        return (
                                                            <g key={`l-connector-${currentIdx}`}>
                                                                <line x1="0" y1={`${currentY}%`} x2="50%" y2={`${currentY}%`}
                                                                      stroke="#ffc107" strokeWidth="2"/>
                                                                <line x1="50%" y1={`${currentY}%`} x2="50%" y2={`${nextY}%`}
                                                                      stroke="#ffc107" strokeWidth="2"/>
                                                                <line x1="50%" y1={`${nextY}%`} x2="100%" y2={`${nextY}%`}
                                                                      stroke="#ffc107" strokeWidth="2"/>
                                                            </g>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 패자결승 */}
                {loserFinals.length > 0 && (
                    <div className="bracket-section mb-4">
                        <div className="section-header bg-info text-white p-2 rounded">
                            <h5 className="mb-0">🥈 패자결승 (Loser Final)</h5>
                        </div>
                        <div className="finals-container mt-2">
                            <div className="finals-matches">
                                {loserFinals.map((seed, idx) => renderMatch(seed, idx))}
                            </div>
                        </div>
                    </div>
                )}

                {/* 최종결승 */}
                {grandFinals.length > 0 && (
                    <div className="bracket-section">
                        <div className="section-header bg-danger text-white p-2 rounded">
                            <h5 className="mb-0">🏅 최종결승 (Grand Final)</h5>
                        </div>
                        <div className="finals-container mt-2">
                            <div className="finals-matches">
                                {grandFinals.map((seed, idx) => renderMatch(seed, idx))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return renderDoubleElimination();
};

export default CompDoubleElimination;
